import { useCallback, useEffect } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import ExpandMoreIcon from '@/assets/icons/icon-expand-more.svg'
import FilterSettingsIcon from '@/assets/icons/icon-filter-settings.svg'
import { Button } from '@/components/ui/button'
import { CalendarRangeDatePicker } from '@/components/ui/calendar/calendar-range-date-picker'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import { MultiSelect } from '@/modules/shifts/ui/dialogs/multi-select.tsx'
import {
  maxDateRange,
  priorityViews,
} from '@/modules/tiktok-research/videos/ui/filters/constants.ts'
import type { TikTokVideosFiltersForm } from '@/modules/tiktok-research/videos/ui/filters/validation.ts'
import { schema } from '@/modules/tiktok-research/videos/ui/filters/validation.ts'
import { handleDateRange } from '@/modules/tiktok-research/videos/utils.ts'
import { classNames } from '@/utils/helpers'

import { countryOptions } from '../../../consts'
import { Switcher } from '../../../ui/Switcher.tsx'

interface Props {
  className?: string
  includePriority?: boolean
  onChange: (filters: TikTokVideosFiltersForm) => void
  value: TikTokVideosFiltersForm
}

export const TikTokVideosFilters = ({
  className,
  includePriority,
  onChange,
  value,
}: Props) => {
  const form = useForm<TikTokVideosFiltersForm>({
    resolver: zodResolver(schema),
    defaultValues: value,
    mode: 'onChange',
  })
  const openContentTypeFiltersDialog = () => {
    openDialog({
      type: DIALOG_TYPE.CONTENT_TYPE_TIKTOK_FILTERS_DIALOG,
      onSubmit: (value) => {
        form.setValue('contentType', value)
        handleChange({ contentType: value })
      },
      initialValues: contentType || {},
    })
  }

  const openStatisticsFiltersDialog = () => {
    openDialog({
      type: DIALOG_TYPE.STATISTICS_TIKTOK_FILTERS_DIALOG,
      onSubmit: (value) => {
        if (!value) {
          return
        }
        form.setValue('statistics', value)
        handleChange({ statistics: value })
      },
      initialValues: statistics,
    })
  }

  const { statistics, contentType } = form.watch()

  useEffect(() => {
    form.reset(value)
  }, [value, form])

  const handleChange = (values: Partial<TikTokVideosFiltersForm>) => {
    onChange({ ...value, ...values })
  }

  const handlePriorityChange = useCallback(
    (priority: string) => {
      onChange({
        ...value,
        priority: priority as TikTokVideosFiltersForm['priority'],
      })
    },
    [onChange, value],
  )

  const handleCountryChange = useCallback(() => {
    onChange({
      ...value,
      countries: form.getValues('countries'),
    })
  }, [form, onChange, value])

  return (
    <FormProvider {...form}>
      <div
        className={classNames('flex flex-col gap-6 2xl:flex-row', className)}
      >
        {includePriority && (
          <Switcher
            onValueChange={handlePriorityChange}
            className="max-sm:w-full max-sm:[&>button]:w-full"
            options={priorityViews}
          />
        )}
        <div className="flex flex-1 gap-4 max-sm:flex-col 2xl:justify-end">
          <Controller
            name="countries"
            control={form.control}
            render={({ field: { value, onChange } }) => (
              <MultiSelect
                className="max-h-10 flex-1 sm:max-w-[200px]"
                options={countryOptions}
                placeholder="Select Country"
                value={value}
                onChange={onChange}
                onClose={handleCountryChange}
                entity="Countries"
                searchPlaceholder="Search Country"
              />
            )}
          />
          <Button
            variant="outline"
            className="max-h-10 flex-1 justify-between sm:max-w-[200px]"
            onClick={openContentTypeFiltersDialog}
          >
            <div className="flex gap-2 font-normal">
              {contentType ? 'Filter Active' : 'Select Filter'}
            </div>
            <ExpandMoreIcon className="w-5" />
          </Button>
          <div className="flex gap-4">
            <Button
              className="shrink-0"
              variant={statistics ? 'default' : 'outline'}
              onClick={openStatisticsFiltersDialog}
            >
              <FilterSettingsIcon />
            </Button>
            <Controller
              name="date"
              control={form.control}
              render={({ field: { onChange, value } }) => (
                <CalendarRangeDatePicker
                  date={value}
                  className="w-full"
                  maxDateRange={maxDateRange}
                  setDate={(date) => {
                    const dateRange = handleDateRange(date)

                    onChange(dateRange)
                    handleChange({ date: dateRange })
                  }}
                />
              )}
            />
          </div>
        </div>
      </div>
    </FormProvider>
  )
}
